import {DOM} from "../DOM";
import {FormValidatorParams} from "../../definitions/utils/validator/validator/ValidateFormOptions";
import {ControlsForm} from "../../../../app/controls/Form/Form";

/**
 * Contains helper functions for form validations
 */
export class FormValidatorUtil {

    /**
     * Check if form has valid flag
     * @param form - input form
     */
    public static checkFormIsValid(form: HTMLFormElement): boolean {

        const changed = DOM.Parser.getDataByNameFromElementOrNull('valid', form);
        if (changed === null) { return true; }
        return changed === 'yes';
    }

    /**
     * Find form submit element
     * @param params - form options
     * @private
     */
    public static getFormSubmit(params: FormValidatorParams) : HTMLInputElement|HTMLButtonElement|null {

        if (params.forceSubmitButton) { return params.forceSubmitButton; }
        const submitWithName = params.form.querySelector(`[name="_submit"]`) as HTMLElement;
        const submitWithType = params.form.querySelector(`[type="submit"]`) as HTMLInputElement;
        if (!submitWithName && !submitWithType) { console.warn('Missing form submit element!'); return null; }
        return (submitWithName ?? submitWithType) as HTMLInputElement | HTMLButtonElement;
    }

    /**
     * Check is form has edit flag
     * Edit form need to be changed to process submit
     * @param options - form validation options
     * @private
     */
    public static isEditForm(options: FormValidatorParams) : boolean {
        return options.form.classList.contains('editForm');
    }

    /**
     * Check if form has changed flag for internal use
     * @param options - form validation options
     */
    public static checkFormIsChanged(options: FormValidatorParams): boolean {

        if (!FormValidatorUtil.isEditForm(options)) { return true; }
        const changed = DOM.Parser.getDataByNameFromElementOrNull('changed', options.form);
        if (changed === null) { return true; }
        return changed === 'yes';
    }

    /**
     * Check if form has changed flag for form element
     * @param form - form element
     */
    public static checkFormIsChangedForElement(form: HTMLFormElement): boolean {
        return this.checkFormIsChanged({form: form});
    }

    /**
     * Check if form have inputs changed from default value
     * @param options - form validation options
     */
    public static checkFormChanged(options: FormValidatorParams): boolean {

        if (!FormValidatorUtil.isEditForm(options)) { return true; }
        const inputs = this.getAllInputs(options);
        const formInputsCountOnInit = parseInt(DOM.Parser.getDataByNameFromElement('inputs', options.form));
        if (formInputsCountOnInit !== inputs.length) { return true; }
        const formDataCurrent =  ControlsForm.Controller.getFormData(options.form);

        for (let index = 0; index < inputs.length; ++index) {

            const input = inputs[index];
            if (!input.hasAttribute('data-current')) { continue; }
            const isInteger = input.hasAttribute('data-integer');
            const isCheckbox = input.type === 'checkbox';
            const current = DOM.Parser.getDataByNameFromElementOrNull('current', input);

            const currentValue: string|null|number|boolean = isInteger && current
                ? parseInt(current)
                : (isCheckbox && current ? current === "true" : current);

            const newValue: string|null = formDataCurrent[input.name] ?? (input.value.trim().length > 0 ? input.value.trim() : null);
            if (currentValue !== newValue) { return true; }
        }

        return false;
    }

    /**
     * Update form state
     * Check if all inputs are valid
     * If edit form check if changed
     * @param options - form validator options
     * @private
     */
    public static updateFormState(options: FormValidatorParams) {

        // Changed
        const changed = FormValidatorUtil.checkFormChanged(options);
        options.form.dataset.changed = changed ? 'yes' : 'no';

        // Valid
        let valid = !DOM.Parser.checkFormInvalid(options.form);
        options.form.dataset.valid = valid ? 'yes' : 'no';

        // Update submit
        const submit = FormValidatorUtil.getFormSubmit(options);
        if (!submit) { return; }
        submit.disabled = (FormValidatorUtil.isEditForm(options) && !changed) || !valid;
    }

    /**
     * Get all input elements supported by validator - internal
     * @param options - form validation options
     */
    public static getAllInputs(options: FormValidatorParams) : NodeListOf<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement> {
        return DOM.Parser.getHtmlElementsByQuery("input:not(.dropdown-input):not(.items-placeholder):not(.tomselect-checkbox), textarea, select", options.form, true) as
            NodeListOf<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>;
    }

    /**
     * Get all input elements supported by validator for form element
     * @param form - form element
     */
    public static getAllInputsForFormElement(form: HTMLFormElement) : NodeListOf<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement> {
        return this.getAllInputs({form: form});
    }
}


